import * as React from "react"
import { useQuery } from "react-query"
import dayjs from "dayjs"
import {
  Box,
  Flex,
  Text,
  Avatar,
  Select,
  Heading,
  HStack,
  useDisclosure,
  Button,
  Stack,
  Badge,
} from "@chakra-ui/react"

import { useOrgs } from "../components/providers/OrgProvider"
import { humanize } from "../lib/helpers/text"
import { Table, Column } from "../components/Table"
import { SearchBar } from "../components/SearchBar"
import { TABLE_PAGE_SIZE, TOPBAR_HEIGHT } from "../lib/config"
import { fetcher } from "../lib/fetcher"
import { UserModal } from "../components/UserModal"
import { InviteUser } from "../components/InviteUser"
import { PendingUserInvites, InvitationsResponse } from "../components/PendingUserInvites"
import { Order } from "../lib/types/shared"
import { UserEntity } from "../lib/types/user"
import { ROLE_OPTIONS } from "../lib/static/user"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"

interface UsersResponse {
  count: number
  results: UserEntity[]
}

export const Users = () => {
  useDocumentTitle("Users")

  const { currentOrgs } = useOrgs()
  const [search, setSearch] = React.useState<string>("")

  const [order, setOrder] = React.useState<Order>({
    order: "-",
    orderBy: "created",
  })
  const [role, setRole] = React.useState<string>("")
  const [page, setPage] = React.useState<number>(1)

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  const userUrl = `users/?page_size=${TABLE_PAGE_SIZE}&ordering=${
    order.order + order.orderBy
  }&organizations=${currentOrgsUuids.join()}&query=${search}&role=${role}`

  const getUsers = React.useCallback(
    (currentPage: number) => fetcher(userUrl + "&page=" + currentPage),
    [userUrl],
  )

  const { isLoading, data, refetch } = useQuery<UsersResponse, [string, number]>(
    [userUrl, page],
    () => getUsers(page),
    { keepPreviousData: true },
  )
  const users = data?.results || []

  const {
    data: invitationsData,
    isLoading: invitationsLoading,
    refetch: invitationsRefetch,
    isFetching,
  } = useQuery<InvitationsResponse, string>(
    `invitations/?state=provisioned&page_size=99&organizations=${currentOrgsUuids.join()}`,
  )

  const handleSetRole = (role: string) => {
    setRole(role)
    setPage(1)
  }
  const handleSetSearch = (search: string) => {
    setSearch(search)
    setPage(1)
  }
  const handleSetOrder = (order: Order) => {
    setOrder(order)
    setPage(1)
  }

  return (
    <Box>
      <Flex px={6} justify="space-between" align="center" h={TOPBAR_HEIGHT}>
        <Heading as="h3" color="gray.600">
          Users
        </Heading>
        <Flex justify="flex-end" align="center">
          <HStack spacing={4}>
            <InviteUser invitationsRefetch={invitationsRefetch} />
            {invitationsData && (
              <PendingUserInvites
                invitations={invitationsData}
                isLoading={invitationsLoading}
                isFetching={isFetching}
                refetch={invitationsRefetch}
              />
            )}
            <HStack spacing={2}>
              <Text>Role:</Text>
              <Select w="200px" placeholder="Any" onChange={(e) => handleSetRole(e.target.value)}>
                {ROLE_OPTIONS.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </HStack>
            <SearchBar onSearchSubmit={handleSetSearch} search={search} />
          </HStack>
        </Flex>
      </Flex>
      <Box px={6} pb={6} overflowY="auto" maxHeight={`calc(100vh - ${TOPBAR_HEIGHT})`}>
        <Table
          isLoading={isLoading}
          data={users}
          count={data?.count || 0}
          noData="No users"
          page={page}
          pageSize={TABLE_PAGE_SIZE}
          sort={order}
          onSort={handleSetOrder}
          setPage={setPage}
          rowAlignment="flex-start"
        >
          <Column<UserEntity>
            sortKey="full_name"
            header="NAME"
            row={(user) => <UserItem user={user} onRefetch={refetch} />}
          />
          <Column<UserEntity>
            sortKey="roles"
            header="ROLE"
            flexDirection="column"
            row={(user) =>
              user.roles?.map((role, index) => {
                if (typeof role === "object" && role.organization && currentOrgs.length > 0) {
                  const org = currentOrgs.find((e) => e.url === role.organization)
                  if (org)
                    return (
                      <Flex key={index} my={0.5} align="center">
                        {currentOrgs.length > 1 && <Avatar mr={2} name={org.name} src={org.icon} size="xs" />}
                        <Stack spacing={0} alignItems="start">
                          <Text mr={2} fontSize="0.6rem" whiteSpace="nowrap">
                            {org.name}
                          </Text>
                          <Badge
                            fontSize=".6rem"
                            lineHeight=".65rem"
                            whiteSpace="nowrap"
                            colorScheme="cyan"
                            cursor="default"
                            textAlign="center"
                            variant="subtle"
                            display="inline-block"
                          >
                            {humanize(role.role.toString())}
                          </Badge>
                        </Stack>
                      </Flex>
                    )
                  else return null
                } else return humanize(role.toString()) + (index !== user.roles.length - 1 ? ", " : "")
              })
            }
            maxW="170px"
          />
          <Column<UserEntity>
            header="STATE"
            row={(user) => (
              <Text opacity={user.state === "disabled" ? 0.6 : 1}>
                {user.state === "disabled" ? "Disabled" : "Active"}
              </Text>
            )}
            maxW="80px"
          />
          <Column<UserEntity>
            sortKey="created"
            header="DATE JOINED"
            row={(user) => (
              <Text opacity={user.state === "disabled" ? 0.6 : 1}>
                {dayjs(user.created).format("MMM DD, YYYY")}
              </Text>
            )}
            maxW="150px"
          />
        </Table>
      </Box>
    </Box>
  )
}

interface UserItemProps {
  user: UserEntity
  onRefetch: () => void
}

function UserItem(props: UserItemProps) {
  const modalProps = useDisclosure()

  return (
    <Flex align="flex-start">
      <Avatar name={props.user.display_name} src={props.user.avatar} size="sm" mr={4} />
      <Flex direction="column" align="flex-start">
        <Button variant="link" size="sm" color="gray.600" onClick={modalProps.onOpen}>
          {props.user.display_name}
        </Button>
        <Text fontSize="0.8rem" color="gray.600">
          {props.user.email || "no email"}
        </Text>
      </Flex>
      {modalProps.isOpen && <UserModal {...modalProps} user={props.user} onRefetch={props.onRefetch} />}
    </Flex>
  )
}
